import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { PersonContactGridObject, VoucherGridItemObject, VoucherGridObject, VoucherObject } from '@it2go/types';
import { ComponentStoreFactory } from '../../../../shared/store/common/component-store.factory';

import { selectCurrentService, selectFolderId } from '../../../store/person/person.selectors';
import { PersonService } from 'packages/client/src/app/services/person.service';
import { map } from 'rxjs';

interface State {
    voucher: VoucherObject | null;
    personContacts: PersonContactGridObject | null;
}

const defaultState: State = {
    voucher: null,
    personContacts: null,
};

@Injectable()
export class VoucherDetailPageStore extends ComponentStoreFactory<State> {

    public readonly svc = inject(PersonService);
    folderId$ = this.globalStore!.select(selectFolderId);
    serviceExpertise$ = this.globalStore!.select(selectCurrentService).pipe(
        map((it) => Number(it?.expertise?.code) || 0),
    );
    setVoucher = this.updater((state, voucher: VoucherObject | null) => ({
        ...state,
        voucher,
    }));

    constructor() {
        super(defaultState, inject(Store), inject(PersonService));
    }

    async getLastVoucher(doctorId: string, newIssueDate: Date, editedVoucherId: number | null = null): Promise<VoucherGridItemObject | null> {
        const input = {
            paging: {
                page: 1,
                itemsPerPage: 1,
            },
            filter: [
                [
                    {
                        column: 'doctorId',
                        value: [doctorId],
                        operator: 'EQ',
                    },
                ],
                [
                    {
                        column: 'folderId',
                        value: [String(await this.get(this.folderId$))],
                        operator: 'EQ',
                    },
                ],
                [
                    {
                        column: 'voucherTo',
                        value: [newIssueDate.toISOString()],
                        operator: 'LTE',
                    },
                ],
            ],
            sorter: [
                {
                    column: 'voucherTo',
                    direction: 'DESC',
                },
            ],
        };

        if (editedVoucherId) {
            input.filter.push(
                [
                    {
                        column: 'id',
                        value: [String(editedVoucherId)],
                        operator: 'NEQ',
                    },
                ],
            );
        }

        const res = this.svc.getVouchers(input);
        const vouchers = (await this.get(res)) as unknown as VoucherGridObject;

        return vouchers?.['items']?.[0] ?? null;
    }

    async getNextVoucher(doctorId: string, newIssueDate: Date, editedVoucherId: number | null = null): Promise<VoucherGridItemObject | null> {
        const input = {
            paging: {
                page: 1,
                itemsPerPage: 1,
            },
            filter: [
                [
                    {
                        column: 'doctorId',
                        value: [doctorId],
                        operator: 'EQ',
                    },
                ],
                [
                    {
                        column: 'folderId',
                        value: [String((await this.get(this.folderId$)) || 0)],
                        operator: 'EQ',
                    },
                ],
                [
                    {
                        column: 'voucherTo',
                        value: [newIssueDate.toISOString()],
                        operator: 'GTE',
                    },
                ],
            ],
            sorter: [
                {
                    column: 'issueDate',
                    direction: 'ASC',
                },
                {
                    column: 'voucherTo',
                    direction: 'DESC',
                },
            ],
        };

        if (editedVoucherId) {
            input.filter.push(
                [
                    {
                        column: 'id',
                        value: [String(editedVoucherId)],
                        operator: 'NEQ',
                    },
                ],
            );
        }

        const res = this.svc.getVouchers(input);
        const vouchers = (await this.get(res)) as unknown as VoucherGridObject;

        return vouchers?.['items']?.[0] ?? null;
    }
}
